import {Artifact, DirectSearchResult} from "../../../../../types";
import {resolvedInlineEmailIds} from "./selectedEmail";
import {reorderResultsByMention} from "../../artifacts/mentionOrdering";

export type ResourcesFenceRequest = {artifactId: string; view?: "search"} & (
    | {view: "search"; indices?: never; ids?: never}
    | {indices: number[]; ids?: never}
    | {ids: string[]; indices?: never}
);

export type ResourcesFenceResolution =
    | {status: "pending"}
    | {status: "invalid"}
    | {status: "ready"; artifact: Artifact; results: DirectSearchResult[]; view?: "search"};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const nonEmptyString = (value: unknown): string | null =>
    typeof value === "string" && value.trim().length > 0 ? value.trim() : null;

/** Parse the small, model-authored selector. It carries identifiers only; all
 * labels, links and descriptions are resolved from the registered artifact. */
export function parseResourcesFence(text: string): ResourcesFenceRequest | null {
    let value: unknown;
    try {
        value = JSON.parse(text);
    } catch {
        return null;
    }
    if (!isRecord(value)) return null;
    const artifactId = nonEmptyString(value.artifactId);
    if (!artifactId) return null;
    if ("view" in value && value.view !== "search") return null;
    const view = value.view === "search" ? {view: "search" as const} : {};
    if (value.view === "search" && !("indices" in value) && !("ids" in value)) {
        return {artifactId, view: "search"};
    }
    if ("indices" in value) {
        if ("ids" in value || !Array.isArray(value.indices) || value.indices.length === 0 ||
            !value.indices.every((index) => Number.isSafeInteger(index) && index >= 0)) return null;
        return {artifactId, ...view, indices: [...new Set(value.indices as number[])]};
    }
    // Keep previously saved answers that selected topic IDs readable.
    if (!Array.isArray(value.ids) || value.ids.length === 0) return null;
    const ids = value.ids.map(nonEmptyString);
    if (ids.some((id) => id === null)) return null;
    return {artifactId, ...view, ids: [...new Set(ids as string[])]};
}

function normalizeCandidate(value: unknown): DirectSearchResult | null {
    if (!isRecord(value)) return null;
    const metadata = isRecord(value.metadata) ? value.metadata : undefined;
    const topicId = nonEmptyString(value.topicId) ?? nonEmptyString(metadata?.topicId);
    const title = nonEmptyString(value.title);
    const url = nonEmptyString(value.url);
    if (!topicId || !title || !url) return null;

    const segmentId = nonEmptyString(value.segmentId) ?? nonEmptyString(metadata?.segmentId);
    const id = nonEmptyString(value.id) ?? `${topicId}${segmentId ? `-${segmentId}` : ""}`;
    const description = nonEmptyString(value.description) ?? nonEmptyString(value.snippet) ?? "";
    const score = typeof value.score === "number" && Number.isFinite(value.score) ? value.score : 0;
    const topicType = typeof value.topicType === "string"
        ? value.topicType
        : typeof metadata?.topicType === "string" ? metadata.topicType : undefined;
    const topicTypeid = typeof value.topicTypeId === "number"
        ? value.topicTypeId
        : typeof metadata?.topicTypeid === "number" ? metadata.topicTypeid : undefined;
    const resourceTypeid = typeof value.resourceTypeId === "number"
        ? value.resourceTypeId
        : typeof value.resourceTypeid === "number" ? value.resourceTypeid
        : typeof metadata?.resourceTypeid === "number" ? metadata.resourceTypeid : undefined;

    return {
        id,
        title,
        snippet: description,
        description,
        url,
        score,
        metadata: {
            ...metadata,
            topicId,
            topicType,
            topicTypeid,
            resourceTypeid,
            segmentId: segmentId ?? undefined,
        },
    };
}

function isReferenceOnlySearchArtifact(artifact: Artifact): boolean {
    return artifact.type === "search_results" &&
        artifact.payload?.presentation === "reference_only";
}

const RESOURCES_FENCE = /```resources[ \t]*\r?\n([\s\S]*?)\r?\n```/g;

export function stripResourcesFences(content: string): string {
    return content.replace(/```resources[ \t]*\r?\n[\s\S]*?(?:\r?\n```|$)/g, "");
}

/** Resolve selected ids against a registered reference-only search artifact.
 * Missing data waits only during streaming; a finished answer gets a clear error. */
export function resolveResourcesFence(
    text: string,
    artifacts: Artifact[] | undefined,
    isStreaming: boolean
): ResourcesFenceResolution {
    const request = parseResourcesFence(text);
    if (!request) return isStreaming ? {status: "pending"} : {status: "invalid"};

    const artifact = artifacts?.find((candidate) =>
        candidate.id === request.artifactId && isReferenceOnlySearchArtifact(candidate));
    if (!artifact) return isStreaming ? {status: "pending"} : {status: "invalid"};

    const rawCandidates: unknown[] = Array.isArray(artifact.payload?.results) ? artifact.payload.results : [];
    if (request.view === "search" && !request.indices && !request.ids) {
        const results = rawCandidates.map(normalizeCandidate)
            .filter((item): item is DirectSearchResult => item !== null);
        return {status: "ready", artifact, results, view: "search"};
    }
    let selected: DirectSearchResult[];
    let selectionCount: number;
    if (request.indices) {
        // Resolve the stored index, never the position after filtering malformed entries.
        // Older artifacts without an index use the same positional fallback as backend reuse.
        const byIndex = new Map<number, DirectSearchResult | null>();
        rawCandidates.forEach((raw, position) => {
            if (!isRecord(raw)) return;
            const index = raw.index ?? position;
            if (typeof index !== "number" || !Number.isSafeInteger(index) || index < 0) return;
            byIndex.set(index, byIndex.has(index) ? null : normalizeCandidate(raw));
        });
        selected = request.indices.map((index) => byIndex.get(index))
            .filter((item): item is DirectSearchResult => item != null);
        selectionCount = request.indices.length;
    } else if (request.ids) {
        const candidates = rawCandidates.map(normalizeCandidate)
            .filter((item): item is DirectSearchResult => item !== null);
        const byTopicId = new Map(candidates.map((candidate) => [candidate.metadata?.topicId, candidate]));
        selected = request.ids.map((id) => byTopicId.get(id))
            .filter((item): item is DirectSearchResult => item !== undefined);
        selectionCount = request.ids.length;
    } else {
        return {status: "invalid"};
    }
    if (selected.length !== selectionCount) {
        return isStreaming ? {status: "pending"} : {status: "invalid"};
    }
    return {status: "ready", artifact, results: selected, ...(request.view ? {view: request.view} : {})};
}

export function isReferenceOnlyArtifact(artifact: Artifact): boolean {
    return isReferenceOnlySearchArtifact(artifact);
}

export function artifactsForClipboard(
    visibleCards: Artifact[] | undefined,
    threadArtifacts: Artifact[] | undefined,
    answerContent = ""
): Artifact[] {
    const inlineEmails = resolvedInlineEmailIds(answerContent, threadArtifacts);
    const combined = [
        ...(visibleCards ?? []),
        ...(threadArtifacts ?? []).filter((artifact) => isReferenceOnlyArtifact(artifact) || inlineEmails.has(artifact.id)),
    ];
    const seen = new Set<string>();
    return combined.filter((artifact) => {
        if (seen.has(artifact.id)) return false;
        seen.add(artifact.id);
        return true;
    });
}

export function selectedResultsForClipboard(
    artifact: Artifact,
    answerContent: string
): Artifact[] {
    if (!isReferenceOnlySearchArtifact(artifact)) return [artifact];
    const selected: Artifact[] = [];
    for (const match of answerContent.matchAll(RESOURCES_FENCE)) {
        const resolution = resolveResourcesFence(match[1], [artifact], false);
        if (resolution.status === "ready") {
            selected.push({
                ...artifact,
                payload: {...artifact.payload, results: resolution.view === "search"
                    ? reorderResultsByMention(resolution.results, answerContent)
                    : resolution.results},
            });
        }
    }
    return selected;
}
